/**
 * Error messages and handlers
 */

export const ErrorMessages = {
  NETWORK: 'Network error. Please check your internet connection.',
  PHONE_INVALID: 'Please enter a valid phone number with country code (e.g., +12345678901)',
  CODE_INVALID: 'Invalid or expired verification code. Please try again.',
  CODE_EXPIRED: 'Verification code has expired. Please request a new code.',
  CAMERA_DENIED: 'Camera access denied. Please enable camera in your browser settings.',
  CAMERA_NOT_FOUND: 'No camera found. Please connect a camera and try again.',
  VIDEO_TOO_LARGE: 'Video file is too large. Please record a shorter video.',
  UPLOAD_FAILED: 'Failed to upload video. Please try again.',
  GENERIC: 'Something went wrong. Please try again.',
  REQUIRED_FIELD: 'This field is required',
  NAME_TOO_SHORT: 'Name must be at least 2 characters',
  INVALID_EMAIL: 'Please enter a valid email address'
};

/**
 * Parse error from API response
 * @param {Error|Response|object} error
 * @returns {string} - User-friendly error message
 */
export const parseErrorMessage = (error) => {
  // Network error
  if (!navigator.onLine) {
    return ErrorMessages.NETWORK;
  }
  
  // Extract error message from various formats
  let errorMessage = '';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.data?.message) {
    errorMessage = error.data.message;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  }
  
  // Convert technical errors to user-friendly messages
  return humanizeError(errorMessage) || ErrorMessages.GENERIC;
};

/**
 * Convert technical error messages to user-friendly ones
 * @param {string} errorMessage
 * @returns {string}
 */
const humanizeError = (errorMessage) => {
  if (!errorMessage) return '';
  
  const msg = errorMessage.toLowerCase();
  
  // Network/Connection errors
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('connection')) {
    return 'Unable to connect. Please check your internet connection and try again.';
  }
  
  // Authentication errors
  if (msg.includes('unauthorized') || msg.includes('authentication') || msg.includes('token')) {
    return 'Your session has expired. Please start over.';
  }
  
  // Validation errors
  if (msg.includes('invalid phone') || msg.includes('phone number')) {
    return 'Please enter a valid phone number with country code.';
  }
  
  if (msg.includes('invalid code') || msg.includes('verification code')) {
    return 'Invalid verification code. Please check and try again.';
  }
  
  if (msg.includes('expired') || msg.includes('timeout')) {
    return 'Your request has expired. Please try again.';
  }
  
  // Upload errors
  if (msg.includes('upload') || msg.includes('file')) {
    return 'Failed to upload your video. Please check your connection and try again.';
  }
  
  // Camera errors
  if (msg.includes('camera') || msg.includes('permission')) {
    return 'Camera access is required. Please enable camera permissions in your browser.';
  }
  
  // Server errors
  if (msg.includes('500') || msg.includes('server error') || msg.includes('internal')) {
    return 'We\'re experiencing technical difficulties. Please try again in a few moments.';
  }
  
  if (msg.includes('503') || msg.includes('unavailable')) {
    return 'Service is temporarily unavailable. Please try again shortly.';
  }
  
  // Rate limiting
  if (msg.includes('too many') || msg.includes('rate limit')) {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }
  
  // If it's not too technical, return the original message
  if (!msg.includes('api') && !msg.includes('endpoint') && !msg.includes('/v') && 
      !msg.includes('axios') && !msg.includes('cors') && errorMessage.length < 100) {
    return errorMessage;
  }
  
  return '';
};

/**
 * Handle API errors
 * @param {Error} error
 * @returns {object} - Normalized error object
 */
export const handleApiError = (error) => {
  console.error('API Error:', error);
  
  return {
    message: parseErrorMessage(error),
    originalError: error,
    timestamp: new Date().toISOString()
  };
};

/**
 * Retry utility for failed operations
 * @param {function} fn - Async function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} delay - Delay between retries in ms
 * @returns {Promise}
 */
export const retryOperation = async (fn, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }
  
  throw lastError;
};

/**
 * Log error to external service (placeholder)
 * @param {Error} error
 * @param {object} context
 */
export const logError = (error, context = {}) => {
  // In production, send to error tracking service (Sentry, LogRocket, etc.)
  console.error('Error logged:', {
    error,
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  });
};
